// /mnt/data/PriceChecker.js
import React, { useState, useEffect, useRef } from "react";
import api from "./services/api";

/**
 * PriceChecker (modern compact glass UI)
 * - Keeps original functionality (debounce, suggestions, search API)
 * - Styled to match the provided HTML mock (Space Grotesk, glass, orbs, compact layout)
 *
 * Usage: import PriceChecker from './PriceChecker';
 */

export default function PriceChecker() {
  const [size, setSize] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [results, setResults] = useState([]);
  const [error, setError] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const timeoutRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const ignoreNextFetch = useRef(false);

  useEffect(() => {
    // If we just selected an item, don't re-fetch suggestions for the full name
    if (ignoreNextFetch.current) {
      ignoreNextFetch.current = false;
      return;
    }

    if (size.length < 2) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(async () => {
      try {
        const res = await api.get(
          `/quotations/suggest-item?query=${encodeURIComponent(size)}`
        );
        const data = res.data;
        if (Array.isArray(data)) {
          setSuggestions(data);
          setShowDropdown(true);
        } else {
          setSuggestions([]);
          setShowDropdown(false);
        }
      } catch (err) {
        setSuggestions([]);
        setShowDropdown(false);
      }
    }, 300);

    return () => clearTimeout(timeoutRef.current);
  }, [size]);

  // close dropdown when clicked outside
  useEffect(() => {
    const onDoc = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        inputRef.current &&
        !inputRef.current.contains(e.target)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const handleSearch = async (query = size) => {
    setError("");
    setResults([]);
    setShowDropdown(false);

    if (!query || !query.trim()) {
      setError("Please enter a product to search.");
      return;
    }

    try {
      const res = await api.get(
        `/quotations/product-rate?item=${encodeURIComponent(query)}`
      );
      const data = res.data;

      if (Array.isArray(data) && data.length > 0) {
        setResults(data);
      } else {
        setResults([]);
        setError("No matching results found.");
      }
    } catch (e) {
      setResults([]);
      setError("Server error. Please try again.");
    }
  };

  const handleSelectSuggestion = (sugg) => {
    ignoreNextFetch.current = true; // Prevent re-opening dropdown
    setSize(sugg);
    setSuggestions([]);
    setShowDropdown(false);
    handleSearch(sugg);
  };

  // small helper: format rupee
  const rupee = (v) =>
    Number.isFinite(v) ? Number(v).toLocaleString("en-IN", { maximumFractionDigits: 0 }) : v;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-indigo-950 text-white font-sans">
      {/* Floating Orbs */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        <div
          className="orb absolute w-96 h-96 -top-32 -left-32 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full blur-3xl opacity-40"
          style={{ animation: "float 30s infinite ease-in-out" }}
        />
        <div
          className="orb absolute w-80 h-80 -bottom-32 -right-32 bg-gradient-to-r from-cyan-600 to-indigo-600 rounded-full blur-3xl opacity-40"
          style={{ animation: "float 30s infinite ease-in-out", animationDelay: "-12s" }}
        />
      </div>

      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6 py-12">
        {/* Header */}
        <div className="w-full max-w-3xl mx-auto mb-10 text-center px-10">
          <h1
            className="text-5xl md:text-6xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-400 via-pink-400 to-cyan-400"
            style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}
          >
            Price Check
          </h1>
          <p className="text-lg text-purple-300 mt-2">Latest product rates instantly</p>
        </div>

        {/* Main Card */}
        <div className="w-full max-w-3xl glass rounded-3xl border border-white/10 shadow-2xl p-6">
          {/* Search Input */}
          <div className="relative mb-6">
            <input
              ref={inputRef}
              id="searchInput"
              type="text"
              value={size}
              onChange={(e) => setSize(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSearch();
                }
              }}
              placeholder="Enter Product (e.g. GM BUSH 17X28)"
              className="w-full input-glow bg-white/10 border border-white/20 rounded-xl px-6 py-4 text-base text-purple-100 placeholder-purple-400 focus:border-purple-400 transition-all"
              style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}
              autoComplete="off"
            />
            <div className="absolute right-4 top-1/2 -translate-y-1/2 text-purple-400 pointer-events-none">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>

            {/* Suggestions dropdown styled like design */}
            {showDropdown && suggestions.length > 0 && (
              <div
                ref={dropdownRef}
                className="absolute left-0 right-0 mt-2 bg-slate-900/80 border border-white/10 rounded-xl shadow-2xl overflow-hidden z-50"
              >
                <ul className="max-h-56 overflow-y-auto">
                  {suggestions.map((s, idx) => (
                    <li
                      key={idx}
                      onClick={() => handleSelectSuggestion(s)}
                      className="px-6 py-3 text-sm text-purple-100 hover:bg-purple-700/30 cursor-pointer"
                    >
                      {s}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Search button */}
          <button
            onClick={() => handleSearch()}
            className="btn-glow w-full px-12 py-4 bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl font-semibold text-lg shadow-lg transition-all hover:translate-y-[-3px]"
          >
            Search
          </button>

          {/* Compact Results Table */}
          <div className="mt-8 glass rounded-2xl border border-white/10 overflow-hidden text-sm">
            <div className="bg-white/5 px-6 py-4 grid grid-cols-3 text-purple-300 font-medium">
              <div>Item Name</div>
              <div className="text-center">Rate (₹) <span className="text-[10px] block opacity-70 mt-0.5">(Incl. 18% GST)</span></div>
              <div className="text-right flex items-center justify-end">Last Updated</div>
            </div>

            <div id="results" className="px-6 py-6 text-center text-purple-400">
              {results.length === 0 ? (
                <div className="py-6">{error || "Enter a product to search."}</div>
              ) : (
                <div>
                  {results.map((item, idx) => (
                    <div key={idx} className="grid grid-cols-3 items-center py-4 border-t border-white/8">
                      <div className="truncate text-purple-200 font-medium">{item.item_name}</div>
                      <div className="text-center text-emerald-400 text-lg font-bold">₹{rupee(item.rate)}</div>
                      <div className="text-right text-purple-300 text-xs">{new Date(item.updated_at).toLocaleString()}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-2xl font-bold text-purple-300">Avantica Pumps Pvt Ltd</p>
        </div>
      </div>

      {/* Inline styles */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&display=swap');
        body { font-family: 'Space Grotesk', sans-serif; }
        .glass { background: rgba(255,255,255,0.06); backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px); }
        .input-glow:focus { outline: none; box-shadow: 0 0 0 4px rgba(139,92,246,0.25); border-color: #c084fc; }
        .btn-glow:hover { transform: translateY(-3px); box-shadow: 0 15px 30px -10px rgba(168,85,247,0.5); }
        @keyframes float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-30px); } }
        .orb { animation: float 30s infinite ease-in-out; }
      `}</style>
    </div>
  );
}
